// File: src/components/pages/Cancellations.jsx
// Instructor view - Display cancellation requests with approval functionality

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Calendar, Clock, User, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { cancellationAPI } from '../../services/api';
import { AUTH0_NAMESPACE } from '../../utils/constants';
import '../../styles/modules.css';

export default function Cancellations() {
  const { user, getAccessTokenSilently } = useAuth0();
  
  const userRoles = user?.[`${AUTH0_NAMESPACE}/roles`] || [];
  const isServiceProvider = userRoles.includes('ServiceProvider');

  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [selectedRequests, setSelectedRequests] = useState(new Set());
  const [filterStatus, setFilterStatus] = useState('requested');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isServiceProvider) {
      loadCancellationRequests();
    }
  }, [isServiceProvider, filterStatus]);

  const loadCancellationRequests = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await cancellationAPI.getInstructorCancellationRequests(
        filterStatus,
        getAccessTokenSilently
      );

      if (response.success) {
        // Sort by request date (most recent first)
        const sorted = (response.data || []).sort((a, b) => 
          new Date(b.requestedAt) - new Date(a.requestedAt)
        );
        setCancellationRequests(sorted);
      }
    } catch (err) {
      console.error('Failed to load cancellation requests:', err);
      setError('Failed to load cancellation requests. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRequestSelection = (cancellationId) => {
    setSelectedRequests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cancellationId)) {
        newSet.delete(cancellationId);
      } else {
        newSet.add(cancellationId);
      }
      return newSet;
    });
  };

  const handleApproveSelected = async () => {
    if (selectedRequests.size === 0) return;

    if (!window.confirm(`Are you sure you want to approve ${selectedRequests.size} cancellation request${selectedRequests.size > 1 ? 's' : ''}? This will delete the booking${selectedRequests.size > 1 ? 's' : ''} permanently.`)) {
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      const cancellationIds = Array.from(selectedRequests);

      const response = await cancellationAPI.approveMultipleCancellations(
        { cancellationIds },
        getAccessTokenSilently
      );

      if (response.success) {
        const { successCount, failCount } = response.data;
        
        if (failCount === 0) {
          setSuccess(`Successfully approved ${successCount} cancellation request${successCount > 1 ? 's' : ''}!`);
        } else {
          setSuccess(`Approved ${successCount} request${successCount > 1 ? 's' : ''}. ${failCount} failed.`);
        }

        // Clear selections and reload
        setSelectedRequests(new Set());
        await loadCancellationRequests();

        // Auto-hide success message
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      console.error('Approval error:', err);
      setError(`Failed to approve cancellation requests: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDenyRequest = async (cancellationId) => {
    const reason = window.prompt('Enter reason for denial (optional):');
    if (reason === null) return; // User cancelled

    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await cancellationAPI.denyCancellationRequest(
        cancellationId,
        { reason: reason || 'No reason provided' },
        getAccessTokenSilently
      );

      if (response.success) {
        setSuccess('Cancellation request denied');
        await loadCancellationRequests();
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      console.error('Denial error:', err);
      setError(`Failed to deny cancellation request: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatTime = (time24) => {
    const [hour, min] = time24.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${min.toString().padStart(2, '0')} ${period}`;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (!isServiceProvider) {
    return (
      <div className="tab-content-placeholder">
        <h2>Access Denied</h2>
        <p>This page is only accessible to instructors</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bookings-container">
        <div className="loading-state">
          <Clock size={48} className="loading-icon" />
          <h2>Loading cancellation requests...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="bookings-container">
      {/* Success/Error Messages */}
      {success && (
        <div className="booking-success-banner">
          <CheckCircle size={24} />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="booking-error-banner">
          <AlertCircle size={24} />
          <span>{error}</span>
        </div>
      )}

      {/* Header with Filters and Actions */}
      <div className="cancellations-header">
        <h2>Cancellation Requests</h2>
        
        <div className="header-actions">
          {/* Status Filter */}
          <div className="filter-group">
            <label>Status:</label>
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="requested">Pending</option>
              <option value="cancelled">Approved</option>
              <option value="denied">Denied</option>
              <option value="">All</option>
            </select>
          </div>

          {/* Approve Button */}
          {filterStatus === 'requested' && selectedRequests.size > 0 && (
            <button 
              className="btn-approve"
              onClick={handleApproveSelected}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : `Approve (${selectedRequests.size})`}
            </button>
          )}
        </div>
      </div>

      {/* Cancellation Requests List */}
      {cancellationRequests.length === 0 ? (
        <div className="empty-state">
          <AlertCircle size={64} className="empty-icon" />
          <h3>No Cancellation Requests</h3>
          <p>
            {filterStatus === 'requested' && 'No pending cancellation requests'}
            {filterStatus === 'cancelled' && 'No approved cancellations'}
            {filterStatus === 'denied' && 'No denied requests'}
            {!filterStatus && 'No cancellation requests found'}
          </p>
        </div>
      ) : (
        <div className="cancellation-requests-list">
          {cancellationRequests.map((request) => {
            const isSelected = selectedRequests.has(request.cancellationId);
            const isPending = request.status === 'requested';

            return (
              <div 
                key={request.cancellationId} 
                className={`cancellation-card ${isSelected ? 'cancellation-card-selected' : ''}`}
              >
                {/* Checkbox (only for pending requests) */}
                {isPending && (
                  <div className="cancellation-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleRequestSelection(request.cancellationId)}
                    />
                  </div>
                )}

                {/* Request Details */}
                <div className="cancellation-details">
                  {/* Header Row */}
                  <div className="cancellation-header-row">
                    <div className="student-info">
                      <User size={18} />
                      <span className="student-name">{request.studentName}</span>
                    </div>
                    {request.status === 'requested' && (
                      <span className="status-badge status-pending">Pending</span>
                    )}
                    {request.status === 'cancelled' && (
                      <span className="status-badge status-approved">Approved</span>
                    )}
                    {request.status === 'denied' && (
                      <span className="status-badge status-denied">Denied</span>
                    )}
                  </div>

                  {/* Booking Details */}
                  <div className="booking-info-row">
                    <div className="booking-date">
                      <Calendar size={18} />
                      <span>{formatDate(request.date)}</span>
                    </div>
                    <div className="booking-time">
                      <Clock size={18} />
                      <span>{formatTime(request.startTime)} - {formatTime(request.endTime)}</span>
                    </div>
                  </div>

                  {/* Reason */}
                  {request.reason && (
                    <div className="cancellation-reason">
                      <strong>Reason:</strong> {request.reason}
                    </div>
                  )}

                  {/* Denial Reason */}
                  {request.status === 'denied' && request.denialReason && (
                    <div className="cancellation-reason denial-reason">
                      <strong>Denial Reason:</strong> {request.denialReason}
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="cancellation-metadata">
                    <span className="metadata-item">
                      Requested: {formatTimestamp(request.requestedAt)}
                    </span>
                    {request.processedAt && (
                      <span className="metadata-item">
                        Processed: {formatTimestamp(request.processedAt)}
                      </span>
                    )}
                  </div>

                  {/* Actions for individual request */}
                  {isPending && (
                    <div className="cancellation-actions">
                      <button
                        className="btn-deny-single"
                        onClick={() => handleDenyRequest(request.cancellationId)}
                        disabled={isProcessing}
                      >
                        Deny
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}